import { NonEmptyArray } from 'fp-ts/NonEmptyArray';
import { createExperimentalZodFormResolver, lt, showSuccessAlert } from '@mews/framework';
import { TabbedDialogFormProps, useTabbedDialogForm, useTabbedDialogFormControls } from '@mews-commander/form';
import { OptimusProvider, OverlayedLoader } from '@mews-commander/ui';
import { useRepositoryMutation, useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { WithProductId } from '@mews-commander/types';
import { useCurrentService } from '../queries/useCurrentService';
import {
    ProductFormValues,
    useToProductEditFormDefaultValues,
    useProductFormSchema,
    useToProductUpdateParameters,
} from './schemas/productFormSchema';
import { ProductFormStep } from './types';
import { DetailsStepFields } from './steps/DetailsStepFields';
import { AccountingStepFields } from './steps/AccountingStepFields';
import { PricingStepFields } from './steps/PricingStepFields';
import { OptionsStepFields } from './steps/OptionsStepFields';
import { useAccountingSchema } from './schemas/accountingSchema';
import { useDetailsSchema } from './schemas/detailsSchema';
import { productPricingSchema } from './schemas/pricingSchema';
import { productOptionsSchema } from './schemas/optionsSchema';

const PRODUCT_FORM_TABS: NonEmptyArray<ProductFormStep> = ['details', 'pricing', 'options', 'accounting'];
export const useEditProductDialogForm = ({
    initialTab = 'details',
    productId,
}: WithProductId & { initialTab?: ProductFormStep }) => {
    const detailsSchema = useDetailsSchema();
    const accountingSchema = useAccountingSchema();
    return useTabbedDialogForm<ProductFormStep, ProductFormValues>({
        id: 'editProductForm',
        initialTab,
        title: lt('EditProduct'),
        tabs: PRODUCT_FORM_TABS,
        tabLabels: {
            details: lt('DetailPlural'),
            pricing: lt('Pricing'),
            options: lt('Options'),
            accounting: lt('Accounting'),
        },
        tabFields: {
            details: detailsSchema.keyof().options,
            pricing: productPricingSchema.keyof().options,
            options: productOptionsSchema.keyof().options,
            accounting: accountingSchema.keyof().options,
        },
        saveLabel: lt('Create'),
        renderForm: (props) => (
            <OptimusProvider density="spacious">
                <ProductForm productId={productId} {...props} />
            </OptimusProvider>
        ),
    });
};

const ProductForm = ({ productId, ...other }: WithProductId & TabbedDialogFormProps<ProductFormStep>) => {
    const service = useCurrentService();

    const enterpriseId = useCurrentEnterpriseId();
    const query = useRepositorySuspenseQuery(
        'products',
        'getAll',
        { enterpriseIds: [enterpriseId], productIds: [productId] },
        { select: (data) => data.products?.[0] }
    );

    const productFormSchema = useProductFormSchema();
    const toProductEditFormDefaultValues = useToProductEditFormDefaultValues();

    const mutation = useRepositoryMutation('products', 'update', {
        invalidateQueries: { products: ['getAll', 'getOverviewEntries'] },
        onSuccess: () => {
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
        },
    });
    const toProductUpdateParameters = useToProductUpdateParameters({ productId });

    const { renderFormContent } = useTabbedDialogFormControls<ProductFormStep, ProductFormValues>({
        ...other,
        resolver: createExperimentalZodFormResolver(productFormSchema),
        values: query.data ? toProductEditFormDefaultValues(query.data) : undefined,
        onSubmit: async (formValues, { dirtyFields }) => {
            const requestParameters = await toProductUpdateParameters(formValues, dirtyFields);
            await mutation.mutateAsync({ serviceId: service.id, productUpdates: [requestParameters] });
        },
    });

    return renderFormContent(({ matchTabs }) => (
        <>
            <OverlayedLoader show={query.isFetching} />
            {matchTabs({
                details: () => (
                    <DetailsStepFields
                        helpers={{
                            descriptions: null,
                            externalIdentifier: lt('ProductFormInputHintExternalIdentifier'),
                            image: lt('ProductFormInputHintImage'),
                            names: lt('ProductFormInputHintName'),
                        }}
                    />
                ),
                pricing: () => <PricingStepFields disabled={{ pricing: true, consumptionMoment: true }} />,
                options: () => <OptionsStepFields />,
                accounting: () => <AccountingStepFields />,
            })}
        </>
    ));
};
